/**
 * This file contains functions related to handling effects and rules in the application.
 * It includes functions for handling actions to effects, global rules, conditions, and more.
 * @module effects
 */


import moment from 'moment';
import DefaultSettings from '../components/settings';
import { createGridRequest, createRequest, requestApi } from '../helpers/rest';
import { validation as validationConverter } from '../wrappers/helpers/converters';
import { DynamicEvaluate, Evaluate } from './calculate';
import handleEvents from './events';
const _ = require('lodash');
const validityStatus = {
    'true': 'valid',
    'false': 'inValid'
}

const prevalidityStatus = {
    'true': 'V',
    'false': 'I'
}

/**
 * Array containing the current reference IDs.
 * @type {Array}
 */
let currentrefIds = []

/**
 * Handles actions to effects based on field values, conditions, global rules, extras, onEvent, info, kendoProps, name, and EventType.
 * @param {Object} fieldValues - The field values.
 * @param {Object} conditions - The conditions.
 * @param {Object} global - The global rules.
 * @param {Object} extras - The extras.
 * @param {Object} onEvent - The onEvent rules.
 * @param {Object} info - The info.
 * @param {Object} kendoProps - The kendoProps.
 * @param {string} name - The name.
 * @param {string} EventType - The EventType.
 * @returns {Array} - The effects.
 */
export const handleActionsToEffects = ({ fieldValues, conditions, global, extras, onEvent = {}, info = null, kendoProps, name = null, EventType = 'OC' }) => {
    localStorage.removeItem('onEventRules')

    if (global) {
        return handleGlobalRules(fieldValues, conditions, global, extras, onEvent, info, kendoProps, name, EventType)
    }
    else {
        if (conditions) {
            const { rules, triggerRules } = conditions
            const temp = rules && rules.reduce((accum, eachCondition) => {
                let { effects } = handleConditon(eachCondition, fieldValues, extras, triggerRules, onEvent, info, kendoProps, name, EventType)
                return accum.concat(effects)
            }, [])
            return temp
        }
    }
}

/**
 * Handles global rules and conditions to determine effects based on field values, conditions, extras, onEvent, info, kendoProps, name, and EventType.
 * @param {Object} fieldValues - The field values.
 * @param {Object} conditions - The conditions.
 * @param {Object} global - The global rules.
 * @param {Object} extras - The extras.
 * @param {Object} onEvent - The onEvent rules.
 * @param {Object} info - The info.
 * @param {Object} kendoProps - The kendoProps.
 * @param {string} name - The name.
 * @param {string} EventType - The EventType.
 * @returns {Array} - The effects.
 */
export const handleGlobalRules = (fieldValues, conditions, global, extras, onEvent = {}, info = null, kendoProps, name, EventType) => {
    const { rules, triggerRules } = conditions
    const globalEffects = global && global.rules && global.rules.map(eachCondition => {
        return handleConditon(eachCondition, fieldValues, extras, global.triggerRules, onEvent, info, kendoProps, name, EventType)
    }) || []
    let confirmation = false
    //remove duplicate field changes based on priority and convert to an object
    const globalFieldChanges = removeDuplicateEffects(globalEffects);
    confirmation = globalEffects.some(e => e.isConfirmation);


    const individualEffects = rules && rules.reduce((accum, eachCondition) => {
        let { canOverwrite, effects = [], namehandle, isConfirmation } = handleConditon(eachCondition, fieldValues, extras, triggerRules, onEvent, info, kendoProps, name, EventType)
        confirmation = isConfirmation;
        if (canOverwrite) {
            effects.forEach((e, i) => {
                const { name, type } = e
                if (!accum[name]) {
                    accum[name ? name : (namehandle && namehandle.length > 0 && namehandle[i] || name)] = e
                }
            })
        }
        else {
            effects.forEach(e => {
                const { name, type } = e
                if (!accum[name]) {
                    if (globalFieldChanges[name]) accum[name] = globalFieldChanges[name]
                    else { accum[name] = e }
                }
            })
        }
        return accum
    }, {})

    let onEventEffects = {}
    if (localStorage.getItem('onEventRules')) {
        let onEventSet = JSON.parse(localStorage.getItem('onEventRules'))
        onEventEffects = removeDuplicateEffects(JSON.parse(localStorage.getItem('onEventRules')) || [])
        if (onEventSet && onEventSet.length > 0)
            confirmation = onEventSet.some(e => e.isConfirmation);
       // localStorage.removeItem('onEventRules');
    }
    const alleffects = { ...individualEffects, ...onEventEffects }
    let effect = Object.keys(alleffects).map(each => alleffects[each])
    let globaleffect = Object.keys(globalFieldChanges).map(each => globalFieldChanges[each])
    return [{ confirmation: confirmation }, ...effect, ...globaleffect]
}

export const removeDuplicateEffects = (alleffects) => {
    if (alleffects)
        return alleffects.reduce((accum, each) => {
            if (each) {
                const { effects = [] } = each
                effects.forEach(e => {
                    if (e) {
                        const { name, type } = e
                        //handles Priority
                        if (!accum[name]) accum[name] = e
                    }
                })
            }
            return accum
        }, {})
}
/**
 * Handles a single condition to determine effects based on field values, extras, trigger rules, onEvent, info, kendoProps, name, and EventType.
 * @param {Object} eachCondition - The condition object.
 * @param {Object} fieldValues - The field values.
 * @param {Object} extras - The extras.
 * @param {Object} triggerRules - The trigger rules.
 * @param {Object} onEvent - The onEvent rules.
 * @param {Object} info - The info.
 * @param {Object} kendoProps - The kendoProps.
 * @param {string} name - The name.
 * @param {string} EventType - The EventType.
 * @returns {Object} - The result of the condition evaluation.
 */


export const handleConditon = (eachCondition, fieldValues, extras, triggerRules = {}, onEvent = {}, info = null, kendoProps, name, EventType) => {
    if (eachCondition) {
        const { check, uncheck = true, run, ruleId, canOverwrite, isDynamicRule, hasTriggerRule, triggerRuleId, dynamicData = [], fields, eventOn, swappedRuleType = false } = eachCondition
        let status = false;
        let isConfirmation = false
        //    // console.log('check:', check, ruleId)
        // if (ruleId === 421)
        //     debugger
        status = check ? check.every(each => {
            currentrefIds = []
            return handleRules(each, fieldValues, isDynamicRule, dynamicData, fields)
        }) : true
        let effects = []
        let namehandle = []
        if (hasTriggerRule && !status && triggerRuleId) {
            return handleConditon(triggerRules[triggerRuleId], fieldValues, extras, triggerRules, onEvent, info, kendoProps, name, EventType)
        }
        else {
            if ((status || uncheck) || (onEvent && onEvent.rules && onEvent.rules.length > 0)) {
                let evts = (run && run['events']) || null;
                if (status && extras && extras.runEvents && evts && (evts.alertMessage !== null || evts.hasRaiseNotification !== null ||
                    evts.isAutoNotice !== null || evts.noticesId !== null ||
                    evts.isAutoNotification !== null || evts.notificationId !== null)) {
                    isConfirmation = evts.alertMessage && evts.alertMessage.decodeType && evts.alertMessage.decodeType === 'C' ? true : false
                    run['events'] && handleEvents(run['events'], fieldValues, { ...info, 'BusinessRuleId': ruleId })
                }
                const validityRun = run[validityStatus[status]]
                let tempValidityRun = _.cloneDeep(validityRun)
                if (onEvent && onEvent.rules && onEvent.rules.length > 0 && swappedRuleType === false) {
                    let filterRules = onEvent.rules.filter(parent => {
                        return ruleId === parent.preConditionRuleId && (parent.preConditionCheck === prevalidityStatus[status])
                    })
                    if (filterRules && filterRules.length > 0) {
                        handleOnEvent(filterRules, status, fieldValues, extras, triggerRules, onEvent, info, kendoProps, name, EventType)
                    }
                }
                if (localStorage.getItem('BusinessRules')) {
                    let randomnumber = (Math.random() + 1).toString(36).substring(2)
                    let storageBusiness = JSON.parse(localStorage.getItem('BusinessRules')) ? JSON.parse(localStorage.getItem('BusinessRules')) : [];
                    let storageValues = (tempValidityRun && tempValidityRun.length > 0 && tempValidityRun.filter(e => e.type === 'prop')) || [];
                    if (storageValues && storageValues.length > 0) {
                        (storageValues || []).map(each => {
                            each['HasValue'] = each['hasValue'] ? 'Y' : 'N';
                            delete each['hasValue']
                            each['HasFilter'] = each['hasFilter'] ? 'Y' : 'N';
                            delete each['hasFilter']
                            each['SwappedRuleType'] = each['swappedRuleType'] ? 'Y' : 'N';
                            delete each['swappedRuleType']
                            each['ValueType'] = each['valueType'];
                            delete each['valueType']
                            each['IsRequired'] = each['required'] ? 'Y' : 'N';
                            delete each['required']
                            each['IsDisabled'] = each['disabled'] ? 'Y' : 'N';
                            delete each['disabled']
                            each['IsVisible'] = each['isVisible'] ? 'Y' : 'N';
                            delete each['isVisible']
                            each['Value'] = each['value']
                            delete each['value']
                            each['Name'] = each['name']
                            delete each['name']
                            each['Type'] = each['type']
                            delete each['type']
                            each['EventAt'] = moment().format('MM/DD/YYYY, hh:mm:ss a');
                            each['EventGroupId'] = randomnumber;
                            each['AllowedValues'] = (each['allowedValues'] && each['allowedValues'].length > 0) ? each['allowedValues'].toString() : '';
                            delete each['allowedValues']
                            each['RuleId'] = ruleId;
                            each['EventType'] = EventType
                            each['TriggerField'] = name
                            each['Triggerby'] = JSON.parse(localStorage.getItem('profile')).name
                            each['ProjectMessageId'] = ''//status && run['events'] && run['events'].alertMessage.toString()
                            each['NotificationId'] = status && run['events'] && run['events'].notificationId
                            each['NoticesId'] = status && run['events'] && run['events'].noticesId
                            each['EventTriggerType'] = 'BR' //get it from sections.js bases on routes with sidebar
                            each['EventMessage'] = ''
                            each['EventActionType'] = 'A'
                        })
                    }
                    storageBusiness = [...storageBusiness, ...storageValues]
                    localStorage.setItem('SaveCurrentRecord', 'Y')
                    localStorage.setItem('BusinessRules', JSON.stringify(storageBusiness))
                }
                //   const validityRun = run[validityStatus[status]]
                if (validityRun && validityRun.length > 0) {
                    effects = validityRun.map(each => {
                        const temp = handleEffects({ ...each, currentrefIds, dynamicData }, fieldValues, status, extras, kendoProps);
                        if (temp) {
                            namehandle.push(each.name)
                            return temp
                        }
                    })
                }
            }
            else effects = []
        }

        return {
            canOverwrite: canOverwrite || false,
            effects: effects,
            status: status,
            namehandle: namehandle,
            isConfirmation: isConfirmation
        }
    }
}
/**
 * Handles onEvent conditions to determine effects based on status, field values, extras, trigger rules, onEvent, info, kendoProps, name, and EventType.
 * @param {Array} onEvtconditions - The onEvent conditions.
 * @param {string} status - The status.
 * @param {Object} fieldValues - The field values.
 * @param {Object} extras - The extras.
 * @param {Object} triggerRules - The trigger rules.
 * @param {Object} onEvent - The onEvent rules.
 * @param {Object} info - The info.
 * @param {Object} kendoProps - The kendoProps.
 * @param {string} name - The name.
 * @param {string} EventType - The EventType.
 * @returns {Array} - The effects.
 */

export const handleOnEvent = (onEvtconditions, status, fieldValues, extras, triggerRules, onEvent, info, kendoProps, name, EventType) => {
    onEvtconditions.sort((a, b) => a.ruleId - b.ruleId).forEach(pre => {
        if ((pre.preConditionCheck && (pre.preConditionCheck === prevalidityStatus[status]))) {
            let preResult = handleConditon(pre, fieldValues, extras, triggerRules, onEvent, info, kendoProps, name, EventType);
            if (preResult && preResult.effects && preResult.effects.length > 0) {
                let stateeffects = localStorage.getItem('onEventRules') && JSON.parse(localStorage.getItem('onEventRules')) || []
                stateeffects.push(preResult)
                localStorage.setItem('onEventRules', JSON.stringify(stateeffects))

            }
        }
    })
    // return [preCondeffects, preCondnamehandle]
}

/**
 * Handles effects based on fieldEffects, fieldValues, status, extras, and kendoProps.
 * @param {Object} fieldEffects - The field effects.
 * @param {Object} fieldValues - The field values.
 * @param {string} status - The status.
 * @param {Object} extras - The extras.
 * @param {Object} kendoProps - The kendoProps.
 
export const handleEffects = (fieldEffects, fieldValues, status, extras, kendoProps) => {
    // Add your code here to handle the effects based on the provided parameters
    
   
    const { type, name, getdatafrom, getValue, read, events, itemfilterval, setLocalStorage, ...otherProps } = fieldEffects
    
    switch (type) {
        case 'load':
            // Handle load effect
            break;
            
        case 'validate':
            // Handle validate effect
            break;
            
        case 'loadtemp':
            // Handle loadtemp effect
            break;
            
        case 'loadValues':
            // Handle loadValues effect
            break;
            
        case 'save':
            // Handle save effect
            break;
            
        case 'saveduplicate':
            // Handle saveduplicate effect
            break;
            
        // Add more cases for other effect types if needed
        
        default:
            // Handle default case
            break;
    }
}
*/

export const handleEffects = (fieldEffects, fieldValues, status, extras, kendoProps) => {
    let tempval = {}
    const { type, name, getdatafrom, getValue, read, events, itemfilterval, setLocalStorage, ...otherProps } = fieldEffects
    // const { setLocalStorage } = otherProps
    switch (type) {
        case 'load':
            return handleLoad(read, fieldValues, extras).then(res => {
                return {
                    name: name,
                    type: 'prop',
                    ...otherProps,
                    ...res
                }
            })

        case 'validate':
            return new Promise(async (resolve) => {
                const res = await handleLoad(read, fieldValues, extras);
                return resolve({
                    name: name,
                    type: 'validateButton',
                    ...res
                })
            })
        case 'loadtemp':
            return handleLoad(read, tempval, extras).then(res => {
                if (res) {
                    return {
                        type: 'grid',
                        ...res
                    }
                }
                else {
                    return {
                        type: 'grid',
                        ...{ data: [] }
                    }
                }
            })
        case 'loadValues':
            const { loadtransformProps, initialdataAttributes } = otherProps
            return {
                name: name,
                type: 'loadDefaultValues',
                transformProps: loadtransformProps,
                datasetval: initialdataAttributes
            }

        case 'save':
        case 'saveduplicate':
            return handleSave(read, fieldValues).then(res => {
                if (res.errors)
                    return {
                        type: 'error',
                        error: res.errors
                    }
                else if (res.returnvalue)
                    return {
                        type: 'initial',
                        error: res.errors
                    }
                else {
                    events && handleEvents(events, fieldValues);
                    // if (setLocalStorage) {
                    //     localStorage.setItem(name, res[name])
                    // }
                    return {
                        type: 'savesuccess',
                        fieldValues: res,
                        ...otherProps
                    }
                }
            })
        case 'edit':
            return {
                type: 'edit',

            }
        case 'initial':
            return handleLoad(read, fieldValues).then(res => {
                return {
                    type: 'initial',
                    ...res
                }
            })
        case 'BRActivity':
            return handleBRActivity(read, (localStorage.getItem('BusinessRules') && JSON.parse(localStorage.getItem('BusinessRules'))) || [], fieldValues).then(res => {
                if (res)
                    return { type: 'BRActivity', value: false }
            })
        case 'count':
            return handleLoad(read, fieldValues).then(res => {
                return {
                    type: 'value',
                    key: name,
                    result: (res && Object.keys(res)) ? Object.keys(res).length : 0
                }
            })
        // case 'getLocalStorageData':
        //     return {
        //         name: name,
        //         type: 'getLocalStorageData',
        //         result: getLocalStorageData(name, fieldValues, getdatafrom, otherProps.getvalue)
        //     }
        case 'loadbybody':
            return handleLoadBody(read, fieldValues).then(res => {
                return {
                    type: 'initial',
                    ...res
                }
            })
        case 'businessrules':
            return handleLoad(read, fieldValues).then(res => {
                return {
                    type: 'businessrules',
                    ...res
                }
            })
        case 'BRHiddenState':
            return handleLoad(read, fieldValues).then(res => {
                if (res) {
                    localStorage.setItem('RecordHiddenState', JSON.stringify(Object.values(res)))
                }
                return {
                    type: 'BRHiddenState',
                    ...res
                }
            })
        case 'validations':
            const { transformProps } = otherProps
            return handleValidations(read, fieldValues).then(res => {
                const temp = validationConverter(res[0], {
                    removesubfieldname: transformProps.removesubfieldname,
                }, Object.keys(fieldValues))
                return {
                    type: 'validations',
                    ...temp
                }
            })
        case 'grid':
            return handleGrid(read, fieldValues, extras, kendoProps).then(res => {
                if (res && !kendoProps) {
                    return {
                        type: 'grid',
                        ...res
                    }
                }
                else if (res && kendoProps) {
                    return {
                        type: 'gridExport',
                        ...res
                    }
                }
                else {
                    return {
                        type: 'grid',
                        ...{ data: [] }
                    }
                }
            })
        // case 'show': //correct: unused
        //     return {
        //         name: name,
        //         type: 'layout',
        //         result: handleShow()
        //     }
        case 'clear':
            return {
                name: name,
                type: 'value',
                result: { value: '' }
            }
        case 'clearItems':
            return {
                name: name,
                type: 'prop',
                result: { items: [] }
            }
        case 'clearValues':
            return {
                type: 'clearList',
                result: {}
            }
        case 'calculate':
            return {
                name: name,
                type: 'prop',
                hasValue: true,
                ...otherProps,
                value: handleCalculations(otherProps, fieldValues, extras)
            }
        case 'arrayloadMultiple':
            return handleLoad(read, fieldValues).then(res => {
                return {
                    name: name,
                    type: 'nestedlistArray',
                    otherProps,
                    ...res
                }
            })
        case 'arrayloadMultipleValues':
            return handleLoad(read, fieldValues).then(res => {
                return {
                    name: name,
                    type: 'nestedlistArrayValues',
                    itemfilterval: itemfilterval,
                    otherProps,
                    ...res
                }
            })
        case 'arraydropdownMultiple':
            return handleLoad(read, fieldValues).then(res => {
                return {
                    name: name,
                    type: 'nesteddropdownArray',
                    otherProps,
                    ...res
                }
            })
        case 'result':
            return {
                name: name,
                type: 'prop',
                ...otherProps,
                value: fieldValues[name]
            }
        case 'resultvalue':
        case 'initialloadextrainfovalue':
        case 'fulldata':
        case 'reportinfo':
            return {
                key: name,
                type: 'value',
                result: fieldValues[name]
            }
        case 'getValue':
            return {
                key: name,
                type: 'value',
                result: { value: fieldValues[getdatafrom][getValue] }
            }
        case 'addlayout':
            return {
                type: 'addlayout',
                name: name
            }
        case 'removelayout':
            return {
                type: 'removelayout',
                name: name
            }
        case 'domainmapper':
            return handleLoad(read, fieldValues, extras).then(res => {
                return {
                    name: name,
                    type: 'domainmapper',
                    ...res
                }
            })
        case 'appendextravalues':
            return handleLoad(read, fieldValues, extras).then(res => {
                return {
                    name: name,
                    getdatafrom: getdatafrom,
                    type: 'appendextravalues',
                    ...res
                }
            })
        default:
            return fieldEffects
    }


}

// export const getLocalStorageData = (name, fieldValues, getdatafrom, getvalue) => {
//     let value
//     if (getvalue === getdatafrom) {
//         value = localStorage.getItem(getvalue) ? JSON.parse(localStorage.getItem(getvalue)) : null
//     }
//     else {
//         value = localStorage.getItem(getdatafrom) && JSON.parse(localStorage.getItem(getdatafrom))[getvalue]
//     }
//     return { ...fieldValues, [name]: value }
// }
/**
 * Handles field rules based on the provided condition, rules, and field values.
 * @param {Object} fieldRules - The field rules.
 * @param {Object} fieldValues - The field values.
 * @param {boolean} isDynamicRule - Indicates if the rule is dynamic.
 * @param {Object} dynamicData - The dynamic data.
 * @param {Object} fields - The fields.
 
export const handleRules = (fieldRules, fieldValues, isDynamicRule = false, dynamicData = {}, fields) => {
    const { condition, rules, hideLocalStorage = false } = fieldRules;

    // Handle different conditions
    switch (condition) {
        case 'and':
            // Handle 'and' condition
            break;
        case 'or':
            // Handle 'or' condition
            break;
        case 'not':
            // Handle 'not' condition
            break;
        case 'equals':
            // Handle 'equals' condition
            break;
        case 'notEquals':
            // Handle 'notEquals' condition
            break;
        case 'greaterThan':
            // Handle 'greaterThan' condition
            break;
        case 'lessThan':
            // Handle 'lessThan' condition
            break;
        case 'greaterThanOrEqual':
            // Handle 'greaterThanOrEqual' condition
            break;
        case 'lessThanOrEqual':
            // Handle 'lessThanOrEqual' condition
            break;
        case 'contains':
            // Handle 'contains' condition
            break;
        case 'notContains':
            // Handle 'notContains' condition
            break;
        case 'startsWith':
            // Handle 'startsWith' condition
            break;
        case 'endsWith':
            // Handle 'endsWith' condition
            break;
        case 'in':
            // Handle 'in' condition
            break;
        case 'notIn':
            // Handle 'notIn' condition
            break;
        case 'isEmpty':
            // Handle 'isEmpty' condition
            break;
        case 'isNotEmpty':
            // Handle 'isNotEmpty' condition
            break;
        case 'isTrue':
            // Handle 'isTrue' condition
            break;
        case 'isFalse':
            // Handle 'isFalse' condition
            break;
        case 'isNullOrUndefined':
            // Handle 'isNullOrUndefined' condition
            break;
        case 'isNotNullOrUndefined':
            // Handle 'isNotNullOrUndefined' condition
            break;
        case 'isDynamic':
            // Handle 'isDynamic' condition
            break;
        default:
            // Handle default case
            break;
    }
}*/
export const handleRules = (fieldRules, fieldValues, isDynamicRule = false, dynamicData = {}, fields) => {
    const { condition, rules, hideLocalStorage = false } = fieldRules;

    // eslint-disable-next-line default-case
    switch (condition) {
        case 'AND':
            let tempAndBoolean = rules.every(each => {
                const { condition } = each
                // console.log("condition=condition", condition)
                if (condition && fieldValues) {
                    return handleRules(each, fieldValues, hideLocalStorage)
                }
                else if (!condition && each.name && fieldValues && !fieldValues.hasOwnProperty(each.name) && isDynamicRule) {
                    const temp = DynamicEvaluate(each, fieldValues, dynamicData, fields);
                    let checkcurrentRefids = localStorage.getItem("checkCurrentRefids") || false
                    localStorage.removeItem("checkCurrentRefids")
                    if (temp && temp.length > 0) {
                        // console.log(each, temp)
                        if (currentrefIds.length === 0)
                            currentrefIds = temp
                        else {
                            let tempRefs = currentrefIds;
                            currentrefIds = [];
                            tempRefs.find(value => {
                                if (temp.includes(value)) {
                                    // console.log(temp, value, "value matched")
                                    currentrefIds.push(value)
                                }
                            })
                        }
                        //  currentrefIds = temp.filter(value => currentrefIds.includes(value))// [...currentrefIds, ...temp[0]] //concat(temp[0])
                        // currentrefIds = currentrefIds.filter((val, index) => { return currentrefIds.indexOf(val) === index })
                    }
                    // console.log("currentrefIds", currentrefIds)
                    if (checkcurrentRefids)
                        return (currentrefIds && currentrefIds.length > 0) ? true : false
                    else return (temp && temp.length > 0) ? true : false
                }
                else {
                    if (fieldValues) {
                        const temp = handleOperators(each, fieldValues, hideLocalStorage)
                        return temp
                    }
                }
            })
            return tempAndBoolean;
        case 'OR':
            let tempBoolean = rules.some(each => {
                const { condition } = each
                if (condition) {
                    return handleRules(each, fieldValues)
                }
                else if (!condition && fieldValues && !fieldValues.hasOwnProperty(each.name) && isDynamicRule) {
                    const temp = DynamicEvaluate(each, fieldValues, dynamicData, fields)
                    if (temp && temp.length > 0) {
                        if (currentrefIds.length === 0)
                            currentrefIds = temp
                        else currentrefIds = currentrefIds.filter(value => temp.includes(value))// [...currentrefIds, ...temp[0]] //concat(temp[0])
                        // currentrefIds = currentrefIds.filter((val, index) => { return currentrefIds.indexOf(val) === index })
                    }
                    //  // console.log("currentrefIds", currentrefIds)
                    return (temp && temp.length > 0) ? true : false
                }
                else {
                    const temp = handleOperators(each, fieldValues)
                    //   // console.log("output", temp, each)
                    return temp
                }
            })
            return tempBoolean;
    }

}

export const handleOperators = (field, fieldValues, hideLocalStorage = false) => {
    const { name, type, value } = field;
    let RecordHiddenState = localStorage.getItem('RecordHiddenState') && !hideLocalStorage ? JSON.parse(localStorage.getItem('RecordHiddenState')) : null
    switch (type) {
        case 'value':
            return Boolean(fieldValues[name])
        case '=':
            if (fieldValues && Object.keys(fieldValues).includes(name)) {
                if (typeof fieldValues[name] === 'number' && typeof value === 'number') {
                    return fieldValues[name] === value
                }
                else if (typeof fieldValues[name] === 'number' && typeof value !== 'number') {
                    return fieldValues[name].toString() === value
                }
                else return fieldValues[name] === value
            }
            else if (name.includes('.')) {
                return value === name.split(".").reduce((res, prop) => { return res && res[prop] }, fieldValues)
            }
            else return name === value
        case '!=':
            if (Object.keys(fieldValues).includes(name)) {
                if (typeof fieldValues[name] === 'number') {
                    return fieldValues[name].toString() !== value
                }
                else return fieldValues[name] !== value
            }
            else return name !== value
        case '>':
            return Number(fieldValues[name]) > Number(value)
        case '>=':
            return Number(fieldValues[name]) >= Number(value)
        case '<':
            return Number(fieldValues[name]) < Number(value)
        case '<=':
            return Number(fieldValues[name]) <= Number(value)
        case 'not in':
            return !(value && name && fieldValues[name] && (value.replace(/[()''\s]/g, '').split(',') || []).some(each => (isNaN(fieldValues[name]) ? fieldValues[name] : fieldValues[name].toString()).split(',').includes(each))) || false
        case 'in':
            return (value && name && fieldValues[name] && (value.replace(/[()''\s]/g, '').split(',') || []).some(each => (isNaN(fieldValues[name]) ? fieldValues[name] : fieldValues[name].toString()).split(',').includes(each))) || false
        case 'is null':
            if (RecordHiddenState && Array.isArray(RecordHiddenState) && RecordHiddenState.length > 0 && name && fieldValues) {
                let IsDisabledObj = RecordHiddenState.find(each => name.split(".").includes(each.EventField)) || {}
                let IsDisabled = (IsDisabledObj && IsDisabledObj.IsDisabled) || null
                // console.log(IsDisabled, name)
                if (IsDisabled && (IsDisabled === 'Y')) {
                    // console.log("disabled", name, false)
                    return false
                }
                else {
                    // console.log("not-disabled", name, (fieldValues[name] === null || fieldValues[name] === "" || fieldValues[name] === undefined) ? true : false)
                    return (fieldValues[name] === null || fieldValues[name] === "" || fieldValues[name] === undefined) ? true : false
                }
            }
            return (!fieldValues[name] || fieldValues[name] === "") ? true : false
        case 'is not null':
            if (RecordHiddenState && Array.isArray(RecordHiddenState) && RecordHiddenState.length > 0 && name && fieldValues) {
                let IsDisabledObj = RecordHiddenState.find(each => each.EventField === name) || {}
                let IsDisabled = (IsDisabledObj && IsDisabledObj.IsDisabled) || null
                //   // console.log(IsDisabled, name)
                if (IsDisabled && (IsDisabled === 'Y'))
                    return true
                else return (fieldValues[name] && Array.isArray(fieldValues[name])) ? fieldValues[name].length > 0 ? true : false : (!fieldValues[name] || fieldValues[name] === '') ? false : true
            }
            return (fieldValues && Array.isArray(fieldValues[name])) ? fieldValues[name].length > 0 ? true : false : (fieldValues && (!fieldValues[name] || fieldValues[name] === '')) ? false : true
        case 'check':
            return fieldValues[name] ? ((fieldValues[name].disabled === 'N' || fieldValues[name].disabled === false) ? true : false ||
                (fieldValues[name] === 'N' || fieldValues[name] === false) ? true : false) : false
        case 'CE':
            return Evaluate(value, fieldValues)
        default:
            return false
    }
}

export const handleLoadBody = (read, fieldValues, extras) => {
    return Promise.all(read.map(each => requestApi(createRequest(each, each.body ? each.body : fieldValues, each.body ? fieldValues : extras)))).then(results => {
        return read.reduce((sum, current, index) => {
            if (current.saveAs) {
                const { saveAs } = current
                return { ...sum, [saveAs.key]: results[index] }
            }
            else return { ...sum, ...results[index] }
        }, {})
    }).catch(ex => {
        // console.log(ex)
    })
}


export const handleBRActivity = (read, data, params) => {
    return Promise.all(read.map(each => requestApi(createRequest(each, data, params)))).then(results => {
        return results[0]
    }).catch(ex => {
        // console.log(ex)
    })
}

/**
 * Handles the load effect.
 * @param {Function} read - The read function.
 * @param {Object} fieldValues - The field values.
 * @param {Object} extras - The extras.
 * @returns {Promise} - A promise that resolves when the load effect is handled.

export const handleLoad = async (read, fieldValues, extras) => {
     try {
        const data = await read(fieldValues, extras);
        // Process the data
        // ...
    } catch (error) {
        // Handle the error
        // ...
    }
}
 */
export const handleLoad = (read, fieldValues, extras) => {
    return Promise.all(read.map(each => requestApi(createRequest(each, fieldValues, extras)))).then(results => {
        return read.reduce((sum, current, index) => {
            if (current.saveAs) {
                const { saveAs, sublist } = current;
                let resp = results
                if (sublist) {
                    resp = results[index][sublist]
                }
                if (saveAs.rename) {
                    const { key } = saveAs.rename;
                    resp = results[0][key]
                }
                else resp = results[index]
                return { ...sum, [saveAs.key]: resp }
            }
            else return { ...sum, ...results[index] }
        }, {})
    }).catch(ex => {
        // console.log(ex)
    })
}

export const handleSave = (read, fieldValues) => {
    return Promise.all(read.map(each => requestApi(createRequest(each, fieldValues)))).then(results => {
        if (results)
            return { ...results[0] }
    }).catch(error => {
        return error.then(errors => {
            if (errors) {
                return { errors: errors }
            }
        })
    })
}

export const handleValidations = (read, fieldValues) => {
    return Promise.all(read.map(each => requestApi(createRequest(each, fieldValues)))).then(results => {

        return results
    })
}

export const handleGrid = (read, fieldValues, extras) => {
    let extra = extras || { skip: 0, take: 20 }
    return Promise.all(read.map(each => requestApi(createGridRequest(each, fieldValues, null, extra)))).then(results => {

        return read.reduce((sum, current, index) => {
            const { saveAs } = current
            return { ...sum, [saveAs.key]: results[index] }
        }, {})
    }).catch(error =>
        console.log(`Error in executing ${error}`))
}

export const handleEnable = (props) => {
    const { status, enable, disable } = props
    if (status) return enable
    else return disable
}

export const handleDisable = (props) => {
    const { status, enable, disable } = props
    if (status) return disable
    else return enable

}

export const handleRequired = (props, status) => {
    return props[status]
}



export const handleUncheck = (props, status) => {
    const { valid, inValid } = props
    if (status)
        return valid
    else return inValid

}

export const handleCalculations = (props, values, extras) => {
    const { operator, operands, attr, quotient } = props;
    // const { dataAttr } = extras
    switch (operator) {
        case '*':
            return (extras && extras.dataAttr && extras.dataAttr[operands]) ?
                (Number(values[quotient]) * Number(extras.dataAttr[operands][attr])) : null //.toFixed(2).toString() 
        default: return false
    }

}
export const handleLayoutConditionURLS = async (check, read, data = null, params) => {
    try {
        const results = await Promise.all(read.map(each => requestApi(createRequest(each, data, params))));
        if (results && results[0])
            return handleRules(check, results[0]);
        else return false;
    } catch (ex) {
        console.log(ex);
    }
}
